import { useRef, useState } from "react";
import { type Api, apiClient } from "../../../services/api";
import type { ConversationEngine } from "../../assistant/conversationEngine";

// The feed a conversation starts from, as the create endpoint receives it: a
// reference (kind + uid), never the feed's PubMed query, which stays server-side.
export type GptFeedScope = NonNullable<
  Api["post"]["/webapp/private/conversations"]["input"]["json"]["feedScope"]
>;

// The ONE conversation a landing-page composer builds up, shared across every
// hook that can create it on first use (files → useChatAttachments, references →
// useChatReferences) and by the question itself, which is streamed by the
// conversation page and therefore needs the row to address it.
//
// Why shared rather than one-per-hook: a file and a playlist reference are both
// FK'd to the conversation, so each hook has to create it if it does not exist
// yet. Two independent creators, each with its own `creationRef`, would mint TWO
// empty conversations when the user attaches a file AND references a playlist on
// the landing page — one keeps the file, the other the playlist, and only one id
// wins the page's transport. The loser is orphaned (invisible in history, which
// filters empty transcripts) holding something the user thinks they attached.
// A single coordinator means the first pick/add/question — whichever it is —
// creates the conversation and the rest reuse it.
//
// `epoch` is the shared invalidation token: the landing page's "back" bumps it
// once (via `reset`) and every consumer's in-flight op sees it change and turns
// into a no-op, so an abandoned upload/add can neither rebind the transport nor
// leave a busy counter stuck. Each consumer still owns its OWN busy/optimistic
// state; this hook owns only the conversation identity + creation.

// The engine is kept with the id: its routes hold the conversation, even after
// the flag flips mid-visit.
export type EnsuredConversation = { id: string; engine: ConversationEngine };

export type EnsureGptConversation = {
  // The conversation things attach to, or null before one exists. Non-null and
  // fixed on the conversation page; created on demand on the landing page.
  target: EnsuredConversation | null;
  // Capture at an operation's start; pass to `ensure` and check via `isStale` so
  // an op abandoned by `reset` mid-flight cannot touch state or the transport.
  epoch: () => number;
  isStale: (epoch: number) => boolean;
  // Resolve the conversation to attach to, creating an empty one on first use.
  // Dedupes concurrent callers through a shared in-flight promise so two picks
  // in the same tick never mint two conversations.
  ensure: (epoch: number) => Promise<EnsuredConversation>;
  // Abandon the created conversation (landing-page "back"): bump the epoch and
  // forget the id + in-flight creation so the next pick/add starts fresh.
  reset: () => void;
};

export function useEnsureGptConversation(
  opts:
    // The conversation page: the row exists, nothing is created here.
    | { conversationId: string; engine: ConversationEngine }
    // The GPT landing page: created on demand, from the Discovery feed the
    // reader came from (`feedScope`) or from nowhere in particular (null).
    | {
        conversationId: null;
        feedScope: GptFeedScope | null;
        engine: ConversationEngine | "pending";
      }
): EnsureGptConversation {
  const epochRef = useRef(0);
  // The in-flight creation, shared across consumers. A ref (not state) so it is
  // readable and writable synchronously within one event, before any re-render —
  // `created` only lands a render after the POST resolves, too late to dedupe
  // two picks fired in the same tick.
  const creationRef = useRef<Promise<EnsuredConversation> | null>(null);
  // Held so a SECOND pick/add reuses the first conversation instead of minting
  // another once the POST has resolved.
  const [created, setCreated] = useState<EnsuredConversation | null>(null);
  const target =
    opts.conversationId === null
      ? created
      : { id: opts.conversationId, engine: opts.engine };

  const ensure = async (epoch: number): Promise<EnsuredConversation> => {
    // Two returns rather than one on `target`: the first also narrows `opts`
    // to the landing shape, the only one that carries `feedScope`.
    if (opts.conversationId !== null)
      return { id: opts.conversationId, engine: opts.engine };
    if (created !== null) return created;
    const creating = opts.engine;
    // Reachable only in the first instants of a visit; the caller reports it.
    if (creating === "pending")
      throw new Error("The conversation engine is not known yet");
    if (creationRef.current === null) {
      const feedScope = opts.feedScope;
      creationRef.current = (
        creating === "assistant"
          ? apiClient
              .post("/assistant/conversations", {
                json: { id: crypto.randomUUID(), feedScope },
              })
              .then(({ body }) => body.id)
          : apiClient
              .post("/webapp/private/conversations", { json: { feedScope } })
              .then(({ body }) => body.conversationId)
      )
        .then((id) => {
          const ensured = { id, engine: creating };
          // Guarded INSIDE the promise, not after awaiting it: the rebind happens
          // here, so a check at the call site would run too late. `reset` nulls
          // `creationRef`, so a promise is only ever shared within one epoch.
          if (epochRef.current === epoch) setCreated(ensured);
          return ensured;
        })
        .catch((error: unknown) => {
          // Clear on failure so a later pick can retry; a rejected promise left
          // cached would make every subsequent attach fail forever.
          creationRef.current = null;
          throw error;
        });
    }
    return creationRef.current;
  };

  const reset = (): void => {
    epochRef.current += 1;
    setCreated(null);
    creationRef.current = null;
  };

  return {
    target,
    epoch: () => epochRef.current,
    isStale: (epoch) => epochRef.current !== epoch,
    ensure,
    reset,
  };
}
